import { DataSchemaItemFragment } from 'generated/resources/graphql'

import { normalizeText } from 'modules/core/lib/stringUtils'
import { type Users } from 'modules/core/store/collections/users'

import { WizardData } from 'modules/employees/pages/ImportProcessPage/components/ImportProcessWizard/WizardData'
import { MappedFields } from 'modules/employees/pages/ImportProcessPage/components/ImportProcessWizard/types'
import validator from 'modules/employees/pages/ImportProcessPage/utils/validators/validator'
import { type Employees } from 'modules/employees/store/collections/employees'

import { MappedValues } from './components/MapValuesModal'

export interface CellCoordinates {
  rowIndex?: number
  columnIndex?: number
}

export interface Cell extends CellCoordinates {
  value: string | undefined
  isValid: boolean
}

export interface Row {
  index?: number
  selected: boolean
  cells?: Array<Cell | undefined>
}

interface AvailableOption {
  name: string
  id: string
}

export interface ReducerState {
  editedFileContent?: WizardData['editedFileContent']
  fields?: DataSchemaItemFragment[]
  mappedFields?: MappedFields[]
  legalEntities?: AvailableOption[]
  locations?: AvailableOption[]
  teams?: AvailableOption[]
  dateFormats?: string[]
  cellCoordinatesWithError?: CellCoordinates[]
  cellCoordinatesWithWarnings?: CellCoordinates[]
  rowsWithError?: number[]
  rowsWithWarnings?: number[]
  rowsByFieldErrors?: ErrorsByField | undefined
  rows?: Array<Row | undefined>
  availableRoleLevels?: AvailableOption[]
  availableNationalities?: AvailableOption[]
  availableCountries?: AvailableOption[]
  employees: Employees
  users: Users
}

export interface ErrorsByField {
  [key: string]: number[] | undefined
}

export type ReducerAction =
  | {
      type: 'setInitialData'
      newState: ReducerState
    }
  | {
      type: 'editCell'
      rowIndex: number
      columnIndex: number
      newValue: string
      isValid: boolean
    }
  | {
      type: 'mapFieldValues'
      fieldName: string
      mappedValues: MappedValues
    }
  | {
      type: 'toggleSelectRow'
      rowIndex: number
    }
  | {
      type: 'selectAllRows'
      selected: boolean
    }

const cellField = (state: ReducerState, index?: number) => {
  const [field] =
    state.mappedFields?.filter((mappedField) => mappedField.columnIndex === index) || []
  return state.fields?.find((x) => x.fieldName === field?.fieldName)
}

const sanitizeString = (value: string) => normalizeText(value)

const realCellValue = (state: ReducerState, fieldName: string, cellValue: string) => {
  const existingEnumValue = enumOptions(state, fieldName).find(
    (x) => sanitizeString(x.name) === sanitizeString(cellValue)
  )
  if (!!existingEnumValue) return existingEnumValue.id

  return cellValue
}

const isCellMapped = (state: ReducerState, columnIndex?: number) => {
  return state?.mappedFields?.some((mappedField) => mappedField.columnIndex === columnIndex)
}

const enumOptions = (state: ReducerState, fieldName: string) => {
  switch (fieldName) {
    case 'legal_entity_id':
      return state.legalEntities || []
    case 'location_id':
      return state.locations || []
    case 'team_id':
      return state.teams || []
    case 'job_catalog_level_id':
      return state.availableRoleLevels || []
    case 'nationality':
      return state.availableNationalities || []
    case 'country':
      return state.availableCountries || []

    default:
      return [{ name: '', id: '' }]
  }
}
const isSameCell = (cellA: CellCoordinates, cellB: CellCoordinates) => {
  return cellA.rowIndex === cellB.rowIndex && cellA.columnIndex === cellB.columnIndex
}

const isCellCoordinateIncluded = (
  cellCoordinates: CellCoordinates[] | undefined,
  cellCoordinate: CellCoordinates
) => {
  return cellCoordinates?.some((cell) => isSameCell(cell, cellCoordinate))
}

const calculateCellValidations = (
  state: ReducerState,
  rowIndex: number | undefined,
  columnIndex: number | undefined,
  isValid: boolean | undefined
) => {
  if (rowIndex === undefined || columnIndex === undefined || isValid === undefined) {
    return {
      cellCoordinatesWithError: state.cellCoordinatesWithError,
      cellCoordinatesWithWarnings: state.cellCoordinatesWithWarnings,
    }
  }

  const cellCoordinates = { rowIndex, columnIndex }
  const { cellCoordinatesWithError, cellCoordinatesWithWarnings } = state

  if (isValid) {
    const updatedErrors = cellCoordinatesWithError?.filter(
      (cell) => !isSameCell(cell, cellCoordinates)
    )
    const updatedWarnings = cellCoordinatesWithWarnings?.filter(
      (cell) => !isSameCell(cell, cellCoordinates)
    )
    return { cellCoordinatesWithError: updatedErrors, cellCoordinatesWithWarnings: updatedWarnings }
  }

  const isCellMandatory = !!cellField(state, columnIndex)?.required

  const updatedErrors = () => {
    if (isCellMandatory) {
      const isIncluded = isCellCoordinateIncluded(cellCoordinatesWithError, cellCoordinates)

      return isIncluded
        ? cellCoordinatesWithError
        : [...(cellCoordinatesWithError || []), cellCoordinates]
    } else {
      return cellCoordinatesWithError
    }
  }

  const updatedWarnings = () => {
    if (!isCellMandatory) {
      const isIncluded = isCellCoordinateIncluded(cellCoordinatesWithWarnings, cellCoordinates)

      return isIncluded
        ? cellCoordinatesWithWarnings
        : [...(cellCoordinatesWithWarnings || []), cellCoordinates]
    } else {
      return cellCoordinatesWithWarnings
    }
  }

  return {
    cellCoordinatesWithError: updatedErrors(),
    cellCoordinatesWithWarnings: updatedWarnings(),
  }
}

const rowsByFieldErrors = (
  cellCoordinatesWithError: CellCoordinates[] | undefined,
  cellCoordinatesWithWarnings: CellCoordinates[] | undefined,
  state: ReducerState
) => {
  const errorsByField: ErrorsByField = {
    first_name: [],
    last_name: [],
    email: [],
    legal_entity_id: [],
    location_id: [],
    team_id: [],
    seniority_calculation_date: [],
    contract_start_date: [],
    job_catalog_level_id: [],
    postal_code: [],
    birthday_on: [],
    contact_name: [],
    contact_number: [],
    phone_number: [],
    nationality: [],
    address_line1: [],
    city: [],
    state: [],
    country: [],
    a3_innuva_code: [],
    a3_nom_code: [],
    salary_amount_in_cents: [],
    ends_on: [],
    working_hours: [],
  }

  cellCoordinatesWithError?.concat(cellCoordinatesWithWarnings || []).forEach((cell) => {
    const fieldName = cellField(state, cell?.columnIndex || 0)?.fieldName

    if (fieldName && cell.rowIndex !== undefined) {
      if (!errorsByField[fieldName]?.includes(cell.rowIndex)) {
        errorsByField[fieldName]?.push(cell.rowIndex)
      }
    }
  })

  return errorsByField
}

const rowsWithError = (cellCoordinatesWithError: CellCoordinates[] = []) => {
  return [
    ...new Set(
      cellCoordinatesWithError.map((cellCoordinates) => {
        return cellCoordinates.rowIndex
      })
    ),
  ].filter((rowIndex) => rowIndex !== undefined)
}

const rowsWithWarnings = (cellCoordinatesWithWarnings: CellCoordinates[] = []) => {
  return [
    ...new Set(cellCoordinatesWithWarnings.map((cellCoordinates) => cellCoordinates.rowIndex)),
  ].filter((rowIndex) => rowIndex !== undefined)
}

export function reducer<_T>(state: ReducerState, action: ReducerAction): ReducerState {
  switch (action.type) {
    case 'setInitialData': {
      const cellsWithError: CellCoordinates[] = []
      const cellsWithWarning: CellCoordinates[] = []

      const rows = action.newState.editedFileContent?.rows?.map((row, rowIndex): Row => {
        const cells = row.map((cell, columnIndex): Cell => {
          let isCellValid = true
          const cellValue = realCellValue(
            action.newState,
            cellField(action.newState, columnIndex)?.fieldName || '',
            cell || ''
          )

          const { isValid } = validator({
            cellValue: cellValue || '',
            fieldType: cellField(action.newState, columnIndex)?.fieldType || '',
            enumOptions: enumOptions(
              action.newState,
              cellField(action.newState, columnIndex)?.fieldName || ''
            ).map((x) => x.id),
            formats: action.newState.dateFormats,
            employees: action.newState.employees,
            users: action.newState.users,
          })

          if (!isValid && isCellMapped(action.newState, columnIndex)) {
            cellField(action.newState, columnIndex)?.required
              ? cellsWithError.push({ rowIndex, columnIndex })
              : cellsWithWarning.push({ rowIndex, columnIndex })

            isCellValid = false
          }

          return {
            rowIndex,
            columnIndex,
            value: cellValue || undefined,
            isValid: isCellValid,
          }
        })

        return {
          index: rowIndex,
          selected: true,
          cells,
        }
      })

      return {
        ...state,
        mappedFields: action.newState.mappedFields,
        fields: action.newState.fields,
        legalEntities: action.newState.legalEntities,
        locations: action.newState.locations,
        teams: action.newState.teams,
        availableRoleLevels: action.newState.availableRoleLevels,
        availableNationalities: action.newState.availableNationalities,
        availableCountries: action.newState.availableCountries,
        dateFormats: action.newState.dateFormats,
        employees: action.newState.employees,
        users: action.newState.users,
        rows,
        cellCoordinatesWithError: cellsWithError,
        cellCoordinatesWithWarnings: cellsWithWarning,
        rowsWithError: rowsWithError(cellsWithError),
        rowsWithWarnings: rowsWithWarnings(cellsWithWarning),
        rowsByFieldErrors: rowsByFieldErrors(cellsWithError, cellsWithWarning, action.newState),
      }
    }
    case 'editCell': {
      if (state.rows === undefined) return state

      const rows = state.rows.map((row, index): Row => {
        let cells = row?.cells

        if (index === action.rowIndex) {
          cells = row?.cells?.map((cell) => {
            if (cell?.columnIndex === action.columnIndex) {
              return {
                ...cell,
                value: action.newValue,
                isValid: action.isValid,
              }
            }
            return cell
          })
        }

        return {
          index: row?.index,
          selected: row?.selected ?? false,
          cells,
        }
      })

      const { cellCoordinatesWithError, cellCoordinatesWithWarnings } = calculateCellValidations(
        state,
        action.rowIndex,
        action.columnIndex,
        action.isValid
      )

      return {
        ...state,
        rows,
        cellCoordinatesWithError,
        cellCoordinatesWithWarnings,
        rowsWithError: rowsWithError(cellCoordinatesWithError),
        rowsWithWarnings: rowsWithWarnings(cellCoordinatesWithWarnings),
        rowsByFieldErrors: rowsByFieldErrors(
          cellCoordinatesWithError,
          cellCoordinatesWithWarnings,
          state
        ),
      }
    }
    case 'mapFieldValues': {
      if (!state.rows) return state

      const cellsWithError: CellCoordinates[] = []
      const cellsWithWarning: CellCoordinates[] = []
      let isCellValid = true
      const rows = state.rows
        .map((row): Row => {
          const cells = row?.cells?.map((cell): Cell => {
            let newValue = realCellValue(
              state,
              cellField(state, cell?.columnIndex)?.fieldName || '',
              cell?.value || ''
            )

            const isCellColumn = cellField(state, cell?.columnIndex)?.fieldName === action.fieldName
            const mappedValue = action.mappedValues?.find(
              (value) => value?.unrecognizedValue === cell?.value
            )
            if (mappedValue && isCellColumn) {
              newValue = mappedValue?.value || newValue
            }

            const { isValid } = validator({
              cellValue: newValue || '',
              fieldType: cellField(state, cell?.columnIndex)?.fieldType || '',
              enumOptions: enumOptions(
                state,
                cellField(state, cell?.columnIndex)?.fieldName || ''
              ).map((x) => x.id),
              formats: state.dateFormats,
              employees: state.employees,
              users: state.users,
            })

            if (!isValid && isCellMapped(state, cell?.columnIndex)) {
              cellField(state, cell?.columnIndex)?.required
                ? cellsWithError.push({ rowIndex: cell?.rowIndex, columnIndex: cell?.columnIndex })
                : cellsWithWarning.push({
                    rowIndex: cell?.rowIndex,
                    columnIndex: cell?.columnIndex,
                  })

              isCellValid = false
            }

            return {
              rowIndex: cell?.rowIndex,
              columnIndex: cell?.columnIndex,
              value: newValue || '',
              isValid: isCellValid,
            }
          })

          return {
            index: row?.index,
            selected: row?.selected ?? false,
            cells,
          }
        })
        .filter(Boolean)

      return {
        ...state,
        cellCoordinatesWithError: cellsWithError,
        cellCoordinatesWithWarnings: cellsWithWarning,
        rowsWithError: rowsWithError(cellsWithError),
        rowsWithWarnings: rowsWithWarnings(cellsWithWarning),
        rowsByFieldErrors: rowsByFieldErrors(cellsWithError, cellsWithWarning, state),
        rows,
      }
    }
    case 'toggleSelectRow': {
      if (state.rows === undefined) return state

      const rows = state.rows.map((row, index) => {
        if (row && index === action.rowIndex) {
          return {
            ...row,
            selected: !row.selected,
          }
        }

        return row
      })

      return {
        ...state,
        rows,
      }
    }
    case 'selectAllRows': {
      if (state.rows === undefined) return state

      const rows = state.rows.map((row) => {
        if (row) {
          return {
            ...row,
            selected: action.selected,
          }
        }

        return row
      })

      return {
        ...state,
        rows,
      }
    }
  }
}
